import { Divider, Input, mergeClasses } from '@fluentui/react-components';
import { Search20Regular } from '@fluentui/react-icons';
import { useFragment, useNovaEventing } from '@nova/react';
import { AppBarEvents } from '@office-start/appbar-events';
import React, { useMemo } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { AppBarErrorBoundary } from './AppBarErrorBoundary';
import { FlyoutSearchBoxID, ViewMode } from './Constants';
import { FlexPanel } from './FlexPanel';
import { KeyNavDir } from './FlexPanel.types';
import { useFlyoutStyles } from './Flyout.styles';
import { useFlyoutContext } from './FlyoutContext';
import { FlyoutFooterLink } from './FlyoutFooterLink';
import { SuggestionsAndSearchRenderer } from './FlyoutSuggestionsSection';
import useAppBarContext from './hooks/useAppBarContext';
import { LaunchTile } from './LaunchTile';
import { behaviorFragment as LaunchTile_launchTileBehavior } from './LaunchTile.types';
import { LazyTileContextMenu } from './LazyTileContextMenu';
/**
 * Popover panel that renders overflow tiles and the users apps that aren't displayed in AppBar
 */
export var Flyout = function () {
    var _a;
    var _b = useFlyoutContext(), launchOptions = _b.launchOptions, userSearchQuery = _b.userSearchQuery, setUserSearchQuery = _b.setUserSearchQuery;
    var emptyStateMessage = launchOptions.emptyStateMessage, linkedEntity = launchOptions.linkedEntity, footerLinks = launchOptions.footerLinks, zIndexOverride = launchOptions.zIndexOverride, flyoutTiles = launchOptions.flyoutTiles, launchTileBehaviorRef = launchOptions.launchTileBehavior, footerLinkFormat = launchOptions.footerLinkFormat, searchEnabled = launchOptions.searchEnabled, suggestionsEnabled = launchOptions.suggestionsEnabled, searchDebounceRate = launchOptions.searchDebounceRate, searchBoxPlaceholderText = launchOptions.searchBoxPlaceholderText, suggestedAppsAddButtonText = launchOptions.suggestedAppsAddButtonText, suggestedAppsPermissionsLinkText = launchOptions.suggestedAppsPermissionsLinkText, suggestedAppsPrivacyLinkText = launchOptions.suggestedAppsPrivacyLinkText, suggestedAppsTermsOfUseLinkText = launchOptions.suggestedAppsTermsOfUseLinkText, suggestedAppsSectionTitle = launchOptions.suggestedAppsSectionTitle, searchNoResultsTitle = launchOptions.searchNoResultsTitle, searchNoResultsMessage = launchOptions.searchNoResultsMessage, searchResultsSectionTitle = launchOptions.searchResultsSectionTitle, installedAppsSectionAriaLabel = launchOptions.installedAppsSectionAriaLabel, suggestedAppsSectionAriaLabel = launchOptions.suggestedAppsSectionAriaLabel, installedAppsAnnouncementMessage = launchOptions.installedAppsAnnouncementMessage, suggestedAppsAnnouncementMessage = launchOptions.suggestedAppsAnnouncementMessage, installedAndSuggestedAppsAnnouncementMessage = launchOptions.installedAndSuggestedAppsAnnouncementMessage, closeFlyout = launchOptions.closeFlyout, onPinStateChange = launchOptions.onPinStateChange, _c = launchOptions.customImportHandler, customImportHandler = _c === void 0 ? function (p) { return p; } : _c;
    var novaEventing = useNovaEventing();
    // Lazy renderer for the Suggested App Tiles
    var LazyFlyoutSearchResultsRenderer = React.useRef(React.lazy(function () {
        return customImportHandler(import(
        /* webpackChunkName: "appbar-control-flyoutsearchresults" */ './FlyoutSearchResults'));
    }));
    var appBarContext = useAppBarContext();
    var styles = useFlyoutStyles();
    var contextMenuRef = React.useRef(null);
    var tileContextMenu = useFragment(LaunchTile_launchTileBehavior, launchTileBehaviorRef).tileContextMenu;
    /**
     * Callback to show the tile context menu
     *
     * @param target Target element to use as the context menu anchor
     * @param pinndedIndex Pinned index value of element or undefined if not pinned
     * @param commands Array of commands supported by the tile
     */
    var showContextMenu = React.useCallback(function (target, pinnedIndex, commands) {
        if (contextMenuRef === null || contextMenuRef === void 0 ? void 0 : contextMenuRef.current) {
            contextMenuRef.current.open(target, pinnedIndex, commands);
        }
    }, []);
    // debounced user search
    var onUserSearch = useDebouncedCallback(function (searchString) {
        setUserSearchQuery(searchString);
        novaEventing.generateEvent({
            event: AppBarEvents.userSearch(function () { return ({ searchString: searchString }); }),
        });
    }, searchDebounceRate != null ? searchDebounceRate : 300);
    var buildLaunchTilesFromTileData = React.useCallback(function (tileData) {
        return tileData === null || tileData === void 0 ? void 0 : tileData.map(function (app, index) {
            var pinStateChangedCallback = function (pinnedState) {
                if (onPinStateChange) {
                    onPinStateChange(app.node.id, pinnedState);
                }
            };
            return (React.createElement(LaunchTile, { viewMode: ViewMode.Flyout, index: index, key: app.node.id, m365App: app.node, onClicked: closeFlyout, launchTileBehavior: launchTileBehaviorRef, onPinStateChange: pinStateChangedCallback, linkedEntity: linkedEntity, onContextMenu: showContextMenu, isListItem: true }));
        });
    }, [
        closeFlyout,
        launchTileBehaviorRef,
        linkedEntity,
        onPinStateChange,
        showContextMenu,
    ]);
    var searchBox = useMemo(function () {
        return searchEnabled ? (React.createElement("div", { className: styles.searchBoxRoot },
            React.createElement(Input, { id: FlyoutSearchBoxID, className: styles.searchBoxInput, contentBefore: React.createElement(Search20Regular, { className: styles.searchBoxIcon }), type: "text", role: "searchbox", placeholder: searchBoxPlaceholderText, onChange: function (e) { return onUserSearch(e.target.value); }, spellCheck: false }))) : undefined;
    }, [
        searchEnabled,
        styles.searchBoxRoot,
        styles.searchBoxInput,
        styles.searchBoxIcon,
        searchBoxPlaceholderText,
        onUserSearch,
    ]);
    var searchTileData = useMemo(function () {
        return (appBarContext === null || appBarContext === void 0 ? void 0 : appBarContext.allAppBarItems)
            ? appBarContext.allAppBarItems.map(function (item) {
                return item.data;
            })
            : flyoutTiles;
    }, [appBarContext.allAppBarItems, flyoutTiles]);
    var overflowTiles = useMemo(function () {
        var _a, _b;
        var tileData = (appBarContext === null || appBarContext === void 0 ? void 0 : appBarContext.overflowItems)
            ? appBarContext.overflowItems.map(function (item) {
                return item.data;
            })
            : flyoutTiles;
        if ((tileData === null || tileData === void 0 ? void 0 : tileData.length) === 0) {
            return undefined;
        }
        var overflowLockedAndPinnedTiles = tileData === null || tileData === void 0 ? void 0 : tileData.filter(function (item) {
            var _a, _b, _c, _d;
            return ((((_a = item === null || item === void 0 ? void 0 : item.node) === null || _a === void 0 ? void 0 : _a.lockedIndex) != null &&
                ((_b = item === null || item === void 0 ? void 0 : item.node) === null || _b === void 0 ? void 0 : _b.lockedIndex) >= 0) ||
                (((_c = item === null || item === void 0 ? void 0 : item.node) === null || _c === void 0 ? void 0 : _c.pinnedIndex) != null &&
                    ((_d = item === null || item === void 0 ? void 0 : item.node) === null || _d === void 0 ? void 0 : _d.pinnedIndex) >= 0));
        });
        var overFlowUnpinnedTiles = tileData === null || tileData === void 0 ? void 0 : tileData.filter(function (item) { return !(overflowLockedAndPinnedTiles === null || overflowLockedAndPinnedTiles === void 0 ? void 0 : overflowLockedAndPinnedTiles.includes(item)); });
        var showDivider = overflowLockedAndPinnedTiles &&
            overFlowUnpinnedTiles &&
            overflowLockedAndPinnedTiles.length > 0 &&
            overFlowUnpinnedTiles.length > 0;
        var flyoutLockedOrPinnedTileControls = (_a = buildLaunchTilesFromTileData(overflowLockedAndPinnedTiles)) !== null && _a !== void 0 ? _a : [];
        var flyoutUnpinnedTileControls = (_b = buildLaunchTilesFromTileData(overFlowUnpinnedTiles)) !== null && _b !== void 0 ? _b : [];
        return (React.createElement(FlexPanel, { className: styles.installedAppsWrapper, keyNavigationDirection: KeyNavDir.gridLinear, horizontal: true, wrap: true, role: "listbox", "aria-label": installedAppsSectionAriaLabel !== null && installedAppsSectionAriaLabel !== void 0 ? installedAppsSectionAriaLabel : '' },
            flyoutLockedOrPinnedTileControls,
            showDivider && (React.createElement(Divider, { className: mergeClasses(styles.dividerCommon, styles.resultsDivider) })),
            flyoutUnpinnedTileControls));
    }, [
        appBarContext.overflowItems,
        flyoutTiles,
        buildLaunchTilesFromTileData,
        styles.installedAppsWrapper,
        styles.dividerCommon,
        styles.resultsDivider,
        installedAppsSectionAriaLabel,
    ]);
    var emptyStateContainer = useMemo(function () {
        return (React.createElement(FlexPanel, { className: mergeClasses(styles.tilesEmptyStateContainer, !!searchEnabled && styles.tilesContainerSearchEnabled) },
            React.createElement("span", { className: styles.tilesEmptyStateMessage }, emptyStateMessage)));
    }, [
        emptyStateMessage,
        searchEnabled,
        styles.tilesContainerSearchEnabled,
        styles.tilesEmptyStateContainer,
        styles.tilesEmptyStateMessage,
    ]);
    var tileContainer = useMemo(function () {
        var _a, _b;
        var tilesContainerClass = mergeClasses(styles.tilesContainer, !!searchEnabled && styles.tilesContainerSearchEnabled);
        if ((userSearchQuery === null || userSearchQuery === void 0 ? void 0 : userSearchQuery.trim().length) > 0) {
            var LazyFlyoutSearchResults = LazyFlyoutSearchResultsRenderer.current;
            return (React.createElement(AppBarErrorBoundary, null,
                React.createElement(React.Suspense, { fallback: "" },
                    React.createElement(LazyFlyoutSearchResults, { userSearchQuery: userSearchQuery, noResultsTitle: searchNoResultsTitle, noResultsMessage: searchNoResultsMessage, searchResultsSectionTitle: searchResultsSectionTitle, suggestedAppsAddButtonText: suggestedAppsAddButtonText, suggestedAppsPermissionsLinkText: suggestedAppsPermissionsLinkText, suggestedAppsPrivacyLinkText: suggestedAppsPrivacyLinkText, suggestedAppsTermsOfUseLinkText: suggestedAppsTermsOfUseLinkText, installedAppsSectionAriaLabel: installedAppsSectionAriaLabel, suggestedAppsSectionAriaLabel: suggestedAppsSectionAriaLabel, installedAppsAnnouncementMessage: installedAppsAnnouncementMessage, suggestedAppsAnnouncementMessage: suggestedAppsAnnouncementMessage, installedAndSuggestedAppsAnnouncementMessage: installedAndSuggestedAppsAnnouncementMessage, installedTiles: searchTileData, launchTileBehavior: launchTileBehaviorRef, linkedEntity: linkedEntity, onResultClick: closeFlyout, onPinStateChange: onPinStateChange, showContextMenu: showContextMenu }))));
        }
        if (!overflowTiles) {
            // there are no overflow tiles so serve up an empty message
            return emptyStateContainer;
        }
        return (React.createElement(FlexPanel, { className: tilesContainerClass },
            overflowTiles,
            suggestionsEnabled && (React.createElement(SuggestionsAndSearchRenderer, { userInSearchMode: false, installedAppCount: (_b = (_a = appBarContext.overflowItems) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0, suggestedAppsSectionTitle: suggestedAppsSectionTitle, suggestedAppsSectionAriaLabel: suggestedAppsSectionAriaLabel }))));
    }, [
        styles.tilesContainer,
        styles.tilesContainerSearchEnabled,
        searchEnabled,
        userSearchQuery,
        overflowTiles,
        suggestionsEnabled,
        (_a = appBarContext.overflowItems) === null || _a === void 0 ? void 0 : _a.length,
        suggestedAppsSectionTitle,
        suggestedAppsSectionAriaLabel,
        searchNoResultsTitle,
        searchNoResultsMessage,
        searchResultsSectionTitle,
        suggestedAppsAddButtonText,
        suggestedAppsPermissionsLinkText,
        suggestedAppsPrivacyLinkText,
        suggestedAppsTermsOfUseLinkText,
        installedAppsSectionAriaLabel,
        installedAppsAnnouncementMessage,
        suggestedAppsAnnouncementMessage,
        installedAndSuggestedAppsAnnouncementMessage,
        searchTileData,
        launchTileBehaviorRef,
        linkedEntity,
        closeFlyout,
        onPinStateChange,
        showContextMenu,
        emptyStateContainer,
    ]);
    var footer = useMemo(function () {
        var _a;
        var linkControls = (_a = footerLinks === null || footerLinks === void 0 ? void 0 : footerLinks.map(function (link) {
            return link && (React.createElement(FlyoutFooterLink, { key: link.id, showButtonView: footerLinks.length > 1, footerLink: link, linkFormat: footerLinkFormat, linkedEntity: linkedEntity, onClicked: closeFlyout }));
        })) === null || _a === void 0 ? void 0 : _a.reverse();
        return React.createElement(React.Fragment, null, linkControls);
    }, [footerLinks, footerLinkFormat, linkedEntity, closeFlyout]);
    var style = {
        zIndex: zIndexOverride,
    };
    return (React.createElement(LazyTileContextMenu, { ref: contextMenuRef, tileContextMenu: tileContextMenu, style: style },
        React.createElement(FlexPanel, { className: styles.root, keyNavigationDirection: KeyNavDir.both, tabbable: true },
            searchBox,
            tileContainer,
            footerLinks && footerLinks.length > 0 && (React.createElement(React.Fragment, null,
                React.createElement(Divider, { className: styles.dividerCommon }),
                React.createElement(FlexPanel, { className: styles.footerContainer, horizontal: true }, footer))))));
};
//# sourceMappingURL=Flyout.js.map